import type { CategoryPrompt } from "../types";

import React from "react";

import {
  Compass,
  ListChecks,
  Mountain,
  PersonStanding,
  ShieldCheck,
  Video,
} from "lucide-react";

export const activitiesOutdoorPrompts: CategoryPrompt[] = [
  {
    icon: <ListChecks />,
    title: "Create Pre-Season Waitlist Campaigns",
    description:
      "Create a page that captures emails 2-3 months before seasonal launches with waitlist pages featuring countdown timers and early-bird discounts.",
    prompt: `Build a pre-season waitlist page that captures emails months before launch using seasonal urgency and early-bird incentives for outdoor/activity products.

**SEASONAL LAUNCH:** e.g., "Summer Beach Gear Collection" OR "Winter Ski Season Essentials" OR "Spring Hiking Line"

**LAUNCH TIMING:** e.g., "Drops April 15th" OR "Available in 8 Weeks" OR "Summer 2026 Launch"

**Waitlist Audience:** Choose one:
- Seasonal enthusiasts (plan ahead types)
- Deal hunters (want best price)
- Previous season buyers (repeat customers)

**Tone & Style:**
- Excited and anticipation-building
- Season-focused imagery and language
- Prep-oriented ("Get ready for...")
- Exclusive insider access feel

**Email Capture Guidelines:**
- **Field Requirements:** Email + optional zip code (for regional launch timing)
- **Placement:** Center hero, above-fold
- **CTA Copy:** "Reserve Early Access" or "Join the Waitlist"
- **Incentive:** "Early birds save 25% at launch"
- **Confirmation:** "You're in! Check email for exclusive preview"

**CTA Guidelines:** Each CTA should:
- Reference seasonal readiness ("Be Ready for Summer")
- Emphasize savings ("Lock In Early-Bird Pricing")
- Use scarcity ("Limited Quantities Available")
- Include season timeframe ("Don't Miss Peak Season")

**Conversion Tactics Use:**
- Countdown to launch date (days:hours)
- Tiered early-bird discounts (first 500 = 25%, next 1000 = 20%)
- Last season sellout story ("Sold out in 3 days last year")
- Weather/season readiness urgency ("Beat the summer rush")
- Sneak peek product reveals
- Referral bonus (share = extra discount or priority access)

**Key Elements to Include:**
- **Hero Section:** Seasonal lifestyle imagery, launch countdown timer, waitlist CTA
- **Season Urgency:** "Summer starts in 90 days - be prepared"
- **Last Year Proof:** "2025 collection sold out in 72 hours" with photos
- **Early-Bird Tiers:** Tiered discount structure (first 500 get 25%, next 1000 get 20%)
- **Waitlist Counter:** "4,127 people already waiting"
- **Product Teasers:** Blurred or partial reveals of 3-4 hero products
- **What's Coming:** Category previews (camping, beach, hiking) without full details
- **Exclusive Perks:** "Waitlist members get: 48-hour early access, launch discount, free shipping"
- **Season Preparation Timeline:** "Sign up now → Get preview May 1st → Shop first April 15th"
- **Social Proof:** Testimonials from last season's customers
- **FAQ:** 3-4 questions (When launches? What discount? How notified? Shipping time?)
- **CTAs:** Hero, after perks section, after social proof, footer (4 total)
- **Referral Section:** "Share & move up the list + extra 5% off"

**Seasonal Messaging Examples:**
- **Summer (Feb-Mar):** "Don't wait until Memorial Day - gear sells out fast"
- **Winter (Sep-Oct):** "First snowfall waits for no one - lock in your gear now"
- **Spring (Jan-Feb):** "Trails open soon - be ready for opening day"
- **Fall (Jul-Aug):** "Peak season is 60 days away - reserve your spot"

**Countdown Timer Strategy:**
- **Primary Timer:** Days until launch (center hero)
- **Milestone Markers:** "Early-bird tier closes in 14 days"
- **Multi-stage:** Countdown to preview → countdown to launch

**Post-Signup Experience:**
- **Confirmation:** "You're #4,128 on the list!"
- **Discount Code Preview:** "Your 25% code arrives 48 hours before launch"
- **Referral Link:** "Share to move up 10 spots per friend"
- **Calendar Reminder:** "Add launch day to your calendar"
- **Drip Campaign Preview:** "Expect sneak peeks starting [date]"

**Trust & Urgency Balance:**
- Limited inventory messaging (not fake scarcity)
- Real previous season data
- Transparent launch dates
- No-spam email promise
- Easy unsubscribe option

**Mobile Optimization:**
- Countdown timer sticky at top
- Single-field email capture
- Vertical tier display
- Quick social sharing

**Goal:** Build maximum qualified email list 2-3 months pre-season by leveraging planning mindset, early-bird savings, and fear of missing peak season availability.`,
  },
  {
    icon: <PersonStanding />,
    title: "Create Activity-Specific Quizzes",
    description:
      "Build a quiz asking about activity type and needs, then show personalized recommendations with explanations, gated behind email capture.",
    prompt: `Build an activity-specific quiz that matches customers to the right outdoor gear based on their activities and needs, gated with email capture.

**ACTIVITY FOCUS:** e.g., "Find Your Perfect Hiking Setup" OR "Which Camping Gear Do You Need?" OR "Your Ideal Water Sports Kit"

**PRODUCT CATEGORIES:** e.g., Backpacks, tents, kayaks, climbing gear (8-15 products to match from)

**Quiz Taker Type:** Choose one:
- Activity beginners (need full guidance)
- Intermediate upgraders (replacing old gear)
- Experienced buyers (specific needs)

**Tone & Style:**
- Outdoor enthusiast peer advice
- Knowledgeable but not gatekeeping
- Adventure-focused and inspiring
- Practical and safety-conscious

**Quiz Question Guidelines:** Each question should:
- Use activity-specific language ("terrain type" not "surface")
- Include visual answer choices when possible
- Build toward specific product attributes
- Feel like expert consultation
- 5-7 total questions maximum

**Email Gate Strategy:**
- Place after question #4 (midpoint commitment)
- Frame as "Get your personalized gear recommendations"
- Offer incentive: "Unlock matches + 20% first-order code"
- Single field: email only
- Trust line: "Join 15,000+ adventurers"

**Results Page Elements:**
- **Personalized Headline:** "Based on your [activity], here's what you need for [their specific use case]"
- **Primary Recommendation (Hero):** Top gear pick with detailed "Why this works for you" explanation
- **Complete Kit Suggestion:** 3-5 complementary products as bundle
- **Alternative Options:** 2-3 other choices at different price points or features
- **Product Cards Include:** Image, price, key specs, terrain/activity badges, reviews, "Add to Cart" CTA
- **Match Reasoning:** "You said [answer], so this [feature] is perfect because..."
- **Comparison Chart:** Recommended vs. alternatives (side-by-side specs)
- **Expert Tip Callouts:** Safety or usage advice specific to their activity
- **Bundle Discount:** "Get the complete kit for 15% off"
- **CTAs on Each Product:** "Shop Recommendation" with urgency
- **Retake Option:** "Need different activity? Retake quiz"

**Conversion Tactics Use:**
- Activity season urgency ("Peak hiking season is 4 weeks away")
- Weather-based scarcity ("Stock up before summer rush")
- Skill progression messaging ("Great starter choice" or "Pro-level upgrade")
- Safety validation ("Recommended by outdoor guides")
- Bundle savings ("Save $47 with complete setup")
- Limited stock on seasonal items

**Key Quiz Questions to Include:**
- **Activity Type:** Hiking, camping, water sports, climbing, cycling
- **Experience Level:** Beginner, intermediate, advanced
- **Trip Duration:** Day trips, weekend, multi-day, extended
- **Terrain/Conditions:** Mountains, trails, lakes, ocean, desert
- **Budget Range:** Budget-friendly, mid-range, premium
- **Priority Feature:** Durability, weight, capacity, weather-resistance
- **Group Size:** Solo, couple, family, group adventures

**Expert Recommendation Format:**
- **Product Name + Model**
- **Perfect For:** "[Their activity] in [their conditions]"
- **Key Features Match:** Specific specs tied to their answers
- **Customer Proof:** "4.9★ from 1,200+ adventurers"
- **In Action:** Real customer photos in similar activities

**Activity-Specific Messaging:**
- **Hiking:** "Trail-tested for your elevation gain"
- **Camping:** "3-season rated for your climate"
- **Water Sports:** "Waterproof to 50ft for your activities"
- **Climbing:** "Safety-certified for your skill level"

**Mobile Optimization:**
- Large tap-friendly answer buttons with icons
- Swipeable product recommendations
- Sticky "View Your Kit" CTA
- Quick-add bundle option

**Goal:** Convert overwhelmed activity enthusiasts into buyers by providing expert-level personalized gear recommendations that match their specific outdoor needs and experience level.`,
  },
  {
    icon: <Compass />,
    title: "Create Problem-Solution Listicles",
    description:
      "Build a listicle addressing common product failures, explaining each problem and linking to products that solve it.",
    prompt: `Build a problem-solution listicle that identifies common gear failures and positions your products as the fix for frustrated outdoor enthusiasts.

**TOPIC:** e.g., "7 Reasons Your Camping Gear Keeps Failing You" OR "5 Hiking Mistakes Ruining Your Adventures" OR "Why Your Water Bottle Isn't Cutting It"

**PRODUCT SOLUTIONS:** e.g., "Our insulated tent solves problem #2" OR "Our trail pack fixes issue #5"

**Reader Type:** Choose one:
- Frustrated with current gear (ready to replace)
- Researching before first purchase (avoiding mistakes)
- Experienced users seeking upgrades

**Tone & Style:**
- Empathetic to gear frustrations
- Expert outdoor advisor voice
- Problem-focused before solution-focused
- Validate their experiences first

**CTA Guidelines:** Each CTA should:
- Reference the specific problem solved ("Fix This Issue")
- Use relief language ("Never Deal With This Again")
- Include proof element ("Tested in [conditions]")
- Escalate in directness throughout article

**Conversion Tactics Use:**
- Real failure scenarios with consequences
- Comparison language ("Unlike flimsy alternatives...")
- Spec-based solutions tied to problems
- Customer testimonials on specific fixes
- Weather/condition testing proof
- Limited stock on problem-solver products

**Key Elements to Include:**
- **Hook:** Relatable failure moment ("You're 5 miles in when your gear fails...")
- **List Items (5-8):** Numbered problem → why it happens → your product as solution
- **Problem Structure Per Item:**
  - Subheading: "Problem #X: [Specific failure]"
  - Detailed explanation with scenario
  - Why this problem is common
  - Consequences if unfixed
  - Your product introduction as "the solution"
  - Spec callout showing how it solves it
  - Customer quote about solving this exact issue
- **Product Callouts:** 1-2 per problem with specs and "Shop Solution" CTA
- **Comparison Boxes:** Your product vs. failed alternatives (2-3 throughout)
- **Expert Validation:** "Field-tested in [extreme conditions]"
- **Problem Clusters:** Group related issues ("Durability failures" vs. "Comfort issues")
- **CTAs:** Soft after problem #2, mid after problem #4-5, strong at conclusion (5-7 total)
- **Solution Summary:** Recap of all products mentioned with bundle option
- **FAQ:** 2-3 questions (Will this work for [condition]? Warranty? Returns?)

**Problem-Solution Examples:**
- **Problem:** "Your tent leaks in light rain" → **Solution:** "Our 3-layer waterproof system (tested to 2000mm)"
- **Problem:** "Pack straps dig into shoulders after hour 2" → **Solution:** "Ergonomic load distribution system with padding"
- **Problem:** "Water bottle leaks all over your gear" → **Solution:** "Triple-seal lid tested leak-free for 10,000 cycles"

**Activity-Specific Problem Framing:**
- **Hiking:** Trail scenarios, elevation challenges, weather exposure
- **Camping:** Setup failures, night-time issues, pack-down problems
- **Water Sports:** Waterproofing fails, saltwater damage, portability
- **Climbing:** Safety failures, weight issues, durability under stress

**Trust-Building Elements:**
- Real failure statistics ("73% of campers experience tent leaks")
- Testing certifications and conditions
- Warranty/guarantee as solution confidence
- Before/after customer experiences
- Expert endorsements or partnerships

**Sidebar Elements:**
- "Quick Reference: Problems & Solutions" chart
- "Complete Fix Kit" bundle showcase
- Related problem articles
- Customer reviews filtered by problem solved

**Mobile Optimization:**
- Collapsible problem sections
- Quick-jump navigation to specific problems
- Sticky "Shop All Solutions" CTA
- Image-heavy problem illustrations

**Goal:** Convert frustrated gear owners into customers by validating their problems, explaining why failures happen, and positioning your products as the tested, reliable solutions they've been searching for.`,
  },
  {
    icon: <Mountain />,
    title: "Create Pre-Order Launch Pages",
    description:
      "Build a pre-order page with compelling imagery and specs, offering zero payment upfront and early-bird discounts to forecast demand.",
    prompt: `Build a pre-order launch page that captures commitments with zero upfront payment and early-bird incentives to validate demand before production.

**PRODUCT LAUNCH:** e.g., "2026 Ultra-Light Backpacking Tent" OR "Next-Gen Waterproof Action Camera" OR "Revolutionary Climbing Harness"

**LAUNCH TIMING:** e.g., "Ships June 2026" OR "Delivery in 12 weeks" OR "Available Summer"

**Pre-Order Audience:** Choose one:
- Early adopters (want newest gear first)
- Deal seekers (motivated by discount)
- Brand loyalists (trust your innovations)

**Tone & Style:**
- Innovation-focused and exciting
- Transparent about timeline
- Confidence in product quality
- Risk-free commitment language

**Pre-Order CTA Guidelines:** Each CTA should:
- Emphasize zero payment now ("Reserve - Pay Later")
- Reference discount ("Lock In 30% Off")
- Use low-commitment language ("Secure Your Spot")
- Include delivery timeline reminder

**Conversion Tactics Use:**
- Tiered early-bird pricing (first 100 = 30%, next 500 = 20%)
- Zero payment upfront messaging
- Limited production run scarcity
- Prototype testing results/reviews
- Countdown to price tier increases
- Waitlist conversion (from earlier campaign)

**Key Elements to Include:**
- **Hero Section:** High-quality product renders/prototypes, launch date, "Reserve Now - $0 Today" CTA
- **Zero Payment Headline:** "Reserve yours today - you won't be charged until it ships"
- **Early-Bird Tiers:** Pricing structure with countdown ("First 100 spots: 30% off - 47 left")
- **Innovation Story:** What makes this product revolutionary (3-4 key innovations)
- **Detailed Specs:** Full technical specifications with comparisons to current models
- **Visual Showcase:** 6-10 product angles, features, in-use scenarios
- **How Pre-Order Works:** 3 steps (Reserve free → Get updates → Pay when ships)
- **Production Timeline:** "Reserve now → Production starts March → Ships June"
- **Field Test Results:** Beta tester reviews, performance data, real-world validation
- **Comparison Chart:** New model vs. current/competitor models
- **Risk-Free Guarantee:** "Cancel anytime before shipping, full refund"
- **Delivery Estimate:** Clear ship date with realistic timeline
- **Early Access Perks:** What pre-order customers get (discount, first delivery, bonus item)
- **Social Proof Counter:** "1,847 already reserved"
- **FAQ:** 5-6 questions (When charged? Can I cancel? Shipping? Warranty? Final price?)
- **CTAs:** Hero, after specs, after testing, after comparison, footer (5-6 total)

**Pricing Display Strategy:**
- **Tier 1 (Limited):** ~~$299~~ $209 (30% off) - "47 of 100 left"
- **Tier 2:** ~~$299~~ $239 (20% off) - "Unlocks after Tier 1"
- **Tier 3:** ~~$299~~ $269 (10% off) - "Standard pre-order"
- **Launch Price:** $299 - "After pre-order closes"
- **Countdown Timer:** Until next tier or pre-order ends

**Innovation Highlights:**
- **Problem Solved:** What current gear fails at
- **Tech Breakthrough:** New material, design, or feature
- **Performance Stats:** Weight reduction, strength increase, efficiency gains
- **Patent/Certification:** "Patent-pending design" or "Tested to [standard]"

**Trust & Transparency:**
- Clear production timeline
- No-risk cancellation policy
- Previous successful launches
- Manufacturing partner credibility
- Realistic delivery dates (avoid overpromising)
- Email updates throughout production

**Beta Tester Section:**
- Real tester names + photos
- Specific use cases ("Tested on Pacific Crest Trail")
- Performance testimonials
- Before/after comparisons
- Video testimonials if available

**Scarcity Mechanisms:**
- Limited production run ("Only 5,000 units in first batch")
- Tier countdown timers
- Live reservation counter
- "Almost gone" alerts at 80% capacity

**Post-Reservation Experience:**
- **Confirmation Page:** "Reserved! You won't be charged until [date]"
- **Reservation Number:** "You're #1,848 in production queue"
- **Update Promise:** "Weekly production updates via email"
- **Referral Incentive:** "Refer 3 friends = extra 5% off"
- **Add-Ons:** Optional accessories at pre-order pricing

**Mobile Optimization:**
- Swipeable product image gallery
- Sticky tier countdown + CTA
- Quick-scroll to specs
- One-tap reserve button

**Goal:** Validate product demand and secure committed buyers through risk-free reservations incentivized by significant early-bird savings, building hype and forecasting production needs.`,
  },
  {
    icon: <Video />,
    title: "Create Beginner Guide Advertorials",
    description:
      "Build a guide page targeting beginners positioning my brand as the honest alternative, with checklist of essentials and direct CTAs to starter kits.",
    prompt: `Build a beginner guide advertorial that educates newcomers while positioning your brand as the trusted, no-BS starter gear solution.

**ACTIVITY GUIDE:** e.g., "Complete Beginner's Guide to Hiking" OR "How to Start Camping (Without Wasting Money)" OR "Your First Kayaking Trip: What You Actually Need"

**STARTER KIT FEATURED:** e.g., "Essential Hiking Starter Bundle" OR "First-Timer Camping Kit"

**Beginner Type:** Choose one:
- Complete novices (first time doing activity)
- Casual triers (testing before committing)
- Budget-conscious starters (avoiding expensive mistakes)

**Tone & Style:**
- Friendly mentor, not condescending expert
- Honest about what's essential vs. optional
- Anti-gatekeeping and welcoming
- Practical over perfectionist

**CTA Guidelines:** Each CTA should:
- Use beginner-friendly language ("Start Here")
- Reference simplicity ("Everything You Need")
- Include reassurance ("Tested by First-Timers")
- Emphasize value ("No Wasted Money")

**Conversion Tactics Use:**
- "What NOT to buy" sections (build trust through honesty)
- Beginner mistake callouts you help avoid
- Simple starter kit vs. piecing together
- Money-back guarantee for first-timers
- Beginner success stories and photos
- Cost comparison (our kit vs. buying separately)

**Key Elements to Include:**
- **Hook:** "Don't make these expensive beginner mistakes..."
- **Honest Reality Check:** What beginners get wrong (builds credibility)
- **Essential vs. Optional:** Clear categorization (don't oversell)
- **Beginner Checklist:** Downloadable checklist of what you actually need
- **Gear Breakdown (5-8 items):**
  - Item name + why beginners need it
  - Common mistakes when buying this
  - What to look for (simplified specs)
  - Your product recommendation with "beginner-friendly because..."
  - Price point with value explanation
- **What NOT to Buy:** 3-4 items beginners waste money on
- **Starter Kit Showcase:** Complete bundle with everything from checklist
- **Bundle Savings:** "Save $87 vs. buying separately"
- **First-Timer Success Stories:** "I followed this guide and..." testimonials
- **Common Beginner Questions:** Answered throughout content
- **Safety Basics:** Essential safety tips for the activity
- **Your First Trip Guide:** Step-by-step what to expect
- **CTAs:** After checklist, after "what not to buy," with starter kit, at conclusion (5-6 total)
- **FAQ:** 4-5 questions (Is this enough? Can I add items? Returns? Difficulty level?)

**Essential Checklist Format:**
- **Must-Have (Start Here):** 5-7 core items
- **Nice-to-Have:** 2-3 optional comfort items
- **Skip for Now:** 2-3 things beginners don't need yet
- **Downloadable PDF:** Email gate for checklist download

**Honest Alternative Positioning:**
- **Transparency:** "Other brands push expensive gear you don't need"
- **Right-Sizing:** "Start simple, upgrade later if you love it"
- **No Gatekeeping:** "You don't need $500 gear to start"
- **Real Talk:** "Here's what actually matters vs. marketing hype"

**Beginner Mistake Examples:**
- "Mistake #1: Buying the most expensive [item] thinking it's better for beginners"
- "Mistake #2: Skipping [essential] because it seems optional"
- "Mistake #3: Getting overwhelmed by features you won't use"

**Starter Kit Advantages:**
- Pre-selected compatibility
- Beginner-tested combinations
- One purchase simplicity
- Bundle discount
- "Everything works together" assurance
- Upgrade path guidance

**Trust-Building for Beginners:**
- "Beginner-approved" badges on products
- First-timer guarantee ("If it's not right, return free")
- No-judgment return policy
- Beginner success rate stats
- Simple setup instructions included
- Customer support for questions

**Post-Guide Elements:**
- **Next Steps:** "After your first trip, consider..."
- **Upgrade Path:** When to invest in better gear
- **Community:** "Join 5,000+ beginners in our group"
- **Content Hub:** Related beginner articles/videos

**Mobile Optimization:**
- Collapsible checklist sections
- Printable checklist button
- Quick-add starter kit
- Tap-to-expand "why you need this"

**Goal:** Convert hesitant beginners into confident first-time buyers by providing honest, non-overwhelming guidance that positions your starter kit as the smart, no-regrets entry point to the activity.`,
  },
  {
    icon: <ShieldCheck />,
    title: "Create Guarantee Landing Pages",
    description:
      "Build a page explaining my warranty program with clear terms and customer success stories, featuring testimonials from customers who used it.",
    prompt: `Build a guarantee landing page that turns your warranty into a competitive advantage by showcasing transparent terms and real customer claim stories.

**GUARANTEE TYPE:** e.g., "Lifetime Warranty on All Gear" OR "100-Day Adventure Guarantee" OR "No-Questions-Asked Replacement Policy"

**COVERAGE SCOPE:** e.g., Manufacturing defects, performance failures, satisfaction guarantee, wear-and-tear protection

**Audience Type:** Choose one:
- Skeptical buyers (need reassurance before purchase)
- Past bad experiences (burned by other brands)
- Premium shoppers (expect quality backing)

**Tone & Style:**
- Confident and straightforward
- No fine-print tricks or gotchas
- Customer-first language
- Proud of standing behind products

**CTA Guidelines:** Each CTA should:
- Reference confidence ("Shop Risk-Free")
- Use guarantee as differentiator ("Protected by Our Promise")
- Include ease messaging ("Easy Claims Process")
- Link to products ("See What's Covered")

**Conversion Tactics Use:**
- Competitor comparison (our guarantee vs. theirs)
- Real claim stories with photos
- Claim success rate ("98% approved within 48 hours")
- Hassle-free process emphasis
- Before/after replacement photos
- Time-based urgency ("All purchases today covered")

**Key Elements to Include:**
- **Hero Section:** Bold guarantee headline, trust badge visual, "Shop with Confidence" CTA
- **What's Covered:** Clear list of what guarantee includes (defects, failures, satisfaction, accidents)
- **How It Works:** 3-4 simple steps (Contact us → Send photo → Get replacement/refund)
- **No Fine Print Promise:** "Seriously, it's this simple" with actual simple terms
- **Coverage Duration:** Lifetime, 100-day, 1-year timeline visualization
- **Real Claim Stories (4-6):**
  - Customer name + photo
  - What happened ("Tent zipper failed on day 3")
  - How we handled it ("Sent replacement overnight, no questions")
  - Outcome ("Back on the trail in 2 days")
  - Photo proof of damage → replacement
- **Success Metrics:** "14,000+ claims honored" or "98% same-day approval"
- **What's NOT Covered:** Honest transparency (normal wear, misuse, theft) builds trust
- **Comparison Chart:** Our guarantee vs. typical warranties vs. competitors
- **Claim Process Details:** Email/phone/portal, response time, shipping options
- **FAQ:** 6-8 questions (Do I need receipt? What about gifts? International? Used gear?)
- **Customer Testimonial Grid:** 8-12 short quotes about guarantee experience
- **Product Category Coverage:** Which products have which guarantees
- **CTAs:** Hero, after how-it-works, after stories, with product links (5-6 total)
- **Guarantee Certificate Visual:** Official-looking badge or document

**Real Claim Story Format:**
- **Customer:** "Jake M., Colorado"
- **Product:** "Alpine Backpack"
- **Issue:** "Shoulder strap tore during 50-mile trek"
- **Timeline:** "Contacted Monday, new pack arrived Wednesday"
- **Quote:** "No hassle, no forms, just great service. I'm a customer for life."
- **Before/After Photos:** Damaged gear → replacement

**Guarantee Term Clarity:**
- **Lifetime Coverage:** "As long as you own it, we've got you"
- **Satisfaction Window:** "100 days to test in real conditions"
- **Manufacturing Defects:** "If it fails, we replace it. Period."
- **Performance Promise:** "If it doesn't perform as promised, return it"

**Trust-Building Elements:**
- No receipt required policy (if applicable)
- Gift coverage ("Even if you didn't buy it from us")
- Quick approval stats ("Average response: 4 hours")
- No shipping fees for claims
- Advance replacement option ("We send first, you send after")
- Lifetime support even after warranty

**Comparison to Industry:**
- **Typical Warranty:** "90 days, defects only, complex claims"
- **Our Guarantee:** "Lifetime, performance + satisfaction, 1-click claims"
- **Competitor A:** Limited coverage details
- **Us:** Full transparency + easier process

**Product-Specific Coverage:**
- **Tents:** Waterproofing, zippers, poles, fabric tears
- **Backpacks:** Straps, zippers, frame, stitching
- **Sleeping Bags:** Insulation, zippers, shell damage
- **Footwear:** Sole separation, material failure, fit issues

**Claims Process Transparency:**
- **Step 1:** Contact via email/phone/chat
- **Step 2:** Describe issue + send photo
- **Step 3:** Instant approval (98% cases)
- **Step 4:** Replacement ships same day
- **Step 5:** Return damaged item (prepaid label)

**Social Proof Section:**
- Claims processed this year counter
- Average response time
- Customer satisfaction with claims: 4.9/5
- Repeat customer rate after claim

**Mobile Optimization:**
- Quick claim button (call or form)
- Swipeable customer stories
- Expandable FAQ sections
- One-tap product browsing

**Goal:** Convert hesitant shoppers into confident buyers by showcasing an industry-leading guarantee backed by real customer stories that prove you actually honor it, turning warranty from fine print into a major purchase motivator.`,
  },
];
